import React from 'react'
import { dayjs } from '@mobi/utils/date'
import { CountdownBadge } from '@mobi/component-library/Feedback/CountdownBadge'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { getKambiCouponBetStatus } from '@core/Areas/KambiSports/helpers/getKambiCouponBetStatus'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Components/BetHistory/context'
import { LiveStatusBadge } from './Components/LiveStatusBadge'

export const CountdownStatus: React.FC<{
  eventStartTime: string | null
  betStatus:
    | BetLoopKambiShareCoupon['bets'][0]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
  displayType?: 'standard' | 'multi-status' | 'multi-leg'
  onCountdownReachingZero?(): void
}> = ({ eventStartTime, betStatus, displayType = 'standard', onCountdownReachingZero }) => {
  const [localStatusOverride, setLocalStatusOverride] = React.useState<'Live'>()

  const couponDisplayContext = React.useContext(KambiHistoryCouponDisplayContext)
  const isCashedOut =
    (couponDisplayContext.type === 'betloop' || couponDisplayContext.type === 'external-share') &&
    !!couponDisplayContext.data?.cashedOutAtUtc

  if (localStatusOverride === 'Live') {
    if (displayType === 'multi-leg') return null
    return <LiveStatusBadge />
  }

  const isExternalShare = couponDisplayContext.type === 'external-share'
  const shouldDisplayDate = displayType !== 'multi-status' && !!eventStartTime
  const betStatusCleaned = getKambiCouponBetStatus(betStatus)

  if (
    shouldDisplayDate &&
    !isExternalShare &&
    !isCashedOut &&
    betStatusCleaned === 'Pending' &&
    dayjs().isBefore(eventStartTime)
  ) {
    const handleCountdownReachingZero =
      couponDisplayContext.type === 'betloop'
        ? () => {
            onCountdownReachingZero?.()
            setLocalStatusOverride('Live')
            couponDisplayContext.utils?.handleDisableCopyBet?.()
          }
        : undefined
    return (
      <CountdownBadge
        badgeSize='S'
        advertisedStartTime={new Date(eventStartTime)}
        onReachingZero={handleCountdownReachingZero}
      />
    )
  }

  if (displayType !== 'multi-leg') {
    return (
      <StatusBadge badgeSize='S' color={betStatusCleaned === 'Won' ? 'success' : 'neutral'}>
        {betStatusCleaned}
      </StatusBadge>
    )
  }

  if (!shouldDisplayDate || (displayType === 'multi-leg' && !isExternalShare)) return null

  return (
    <StatusBadge color='gray' badgeSize='S'>
      {dayjs(eventStartTime).format('D MMM | H:mm')}
    </StatusBadge>
  )
}
